// PD-Konflikt im Partner Directory: Hook für den Zustand und Abschnitt für den Details-Dialog
// der Transportfreigabe (Vertrag v28, PD-Konflikt-Workflow mit Edge-Unterstützung).
// Laufanzeige: Polling über planId wenn run.id fehlt; lastResolve mit Fehleranzeige.
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert, LinearProgress, Checkbox, FormControlLabel, Radio, RadioGroup, Stack, Typography,
} from "@mui/material";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import { MonitorButton } from "../../components/MonitorControls";
import { api, v } from "../../utils/helpers";

const POLL_INTERVAL = 15_000;
// Nach spätestens 10 Minuten endet das Nachfragen (scheitert der Lauf, bliebe der Konflikt sonst endlos im Takt).
const POLL_MAX = 10 * 60_000;

const RADIO_SX = { color: "var(--iio-text-2)", "&.Mui-checked": { color: "var(--iio-signal)" } };

/** Baut die Query-Parameter für den run-status-Endpunkt zusammen. */
function runStatusQuery(spaceId, packageId, resolveRunId, planId) {
  const params = new URLSearchParams({ spaceId, packageId });
  if (resolveRunId) params.set("resolveRunId", String(resolveRunId));
  else if (planId) params.set("planId", planId);
  return params.toString();
}

/**
 * Hook: lädt und verwaltet PD-Konflikte für einen fehlgeschlagenen Lauf.
 * Einzige Quelle des Zustands je Karte (kein zweiter Fetch-Loop).
 */
export function usePdConflicts({ spaceId, packageId, runId, enabled = true }) {
  const [conflicts, setConflicts] = useState(null);
  const [canResolve, setCanResolve] = useState(false);
  const [loading, setLoading] = useState(false);
  const [readError, setReadError] = useState(null);
  const readController = useRef(null);
  const readVersion = useRef(0);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [targetStage, setTargetStage] = useState(null);
  // Ergebnis des letzten abgeschlossenen Auflösungslaufs (aus lastResolve oder nach Polling-Ende).
  const [lastResolveStatus, setLastResolveStatus] = useState(null);
  const mountedRef = useRef(true);
  // Lauf-ID oder Plan-ID des gestarteten Auflösungslaufs (für das leichtgewichtige Polling).
  const resolveRunRef = useRef(null);
  const planIdRef = useRef(null);

  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; readController.current?.abort(); readVersion.current += 1; }; }, []);

  const fetchConflicts = useCallback(async () => {
    if (!mountedRef.current || !spaceId || !packageId || !runId || !enabled) return;
    readController.current?.abort();
    const controller = new AbortController();
    readController.current = controller;
    const version = ++readVersion.current;
    const active = () => mountedRef.current && !controller.signal.aborted && version === readVersion.current;
    const readOptions = { signal: controller.signal, retry: false };
    setReadError(null);
    setLoading(true);
    try {
      const data = await api(`/api/cicd/pd-conflicts?spaceId=${encodeURIComponent(spaceId)}&packageId=${encodeURIComponent(packageId)}&runId=${encodeURIComponent(runId)}`, readOptions);
      if (!active()) return;
      if (data.pdErrors?.length) throw Object.assign(new Error("Incomplete Partner Directory read"), {
        code: "PD_READ_INCOMPLETE", issues: data.pdErrors, runtimeChecks: data.runtimeChecks || [],
      });
      const list = data.conflicts || [];
      setConflicts(list);
      setCanResolve(data.canResolve === true);
      setTargetStage(data.targetStage || null);
      if (list.length === 0) setRunning(false);

      // lastResolve: wenn vorhanden, einmal den Status abfragen (Vertrag: Laufanzeige beim Laden).
      const lr = data.lastResolve;
      if (lr && list.length > 0) {
        const qs = runStatusQuery(spaceId, packageId, lr.resolveRunId, lr.planId);
        try {
          const status = await api(`/api/cicd/pd-conflicts/run-status?${qs}`, readOptions);
          if (!active()) return;
          if (status.status === "queued" || status.status === "in_progress") {
            // Lauf noch aktiv: Polling aufnehmen.
            resolveRunRef.current = status.id || lr.resolveRunId || null;
            planIdRef.current = lr.planId || null;
            setResult({ run: { id: status.id, html_url: status.html_url } });
            setRunning(true);
          } else if (status.status === "completed") {
            if (status.conclusion === "success") {
              // Erfolgreich: nichts Besonderes anzeigen, Konflikte sind schon aktuell.
              setLastResolveStatus(null);
            } else {
              // Fehlgeschlagen: Fehlerabschnitt anzeigen.
              setLastResolveStatus({ ...status, createdAt: lr.createdAt, runtimes: lr.runtimes });
            }
          }
        } catch (error) {
          if (active()) setReadError(error);
        }
      } else if (!lr) {
        setLastResolveStatus(null);
      }
    } catch (error) {
      if (!active()) return;
      setReadError(error);
      setCanResolve(false);
    } finally {
      if (active()) setLoading(false);
    }
  }, [spaceId, packageId, runId, enabled]);

  useEffect(() => {
    setConflicts(null);
    setCanResolve(false);
    setReadError(null);
    setLoading(false);
    setRunning(false);
    setResult(null);
    setLastResolveStatus(null);
    resolveRunRef.current = null;
    planIdRef.current = null;
    fetchConflicts();
    return () => { readController.current?.abort(); readVersion.current += 1; };
  }, [fetchConflicts]);

  // Polling im Running-Zustand: prüft den Laufstatus über den leichtgewichtigen
  // Endpunkt /api/cicd/pd-conflicts/run-status statt die Konfliktliste (vermeidet Tenant-Lesezugriffe).
  // Sobald der Lauf abgeschlossen ist, werden die Konflikte einmal neu geladen.
  useEffect(() => {
    if (!running || !spaceId) return undefined;
    const resolveRunId = resolveRunRef.current;
    const planId = planIdRef.current;
    // Ohne Lauf-ID UND ohne Plan-ID kann nicht gepollt werden: Timeout abwarten.
    if (!resolveRunId && !planId) return undefined;
    const ende = Date.now() + POLL_MAX;
    const controller = new AbortController();
    let inFlight = false;
    const timer = setInterval(async () => {
      if (!mountedRef.current) { clearInterval(timer); return; }
      if (inFlight) return;
      if (Date.now() > ende) { clearInterval(timer); setRunning(false); fetchConflicts(); return; }
      inFlight = true;
      try {
        const qs = runStatusQuery(spaceId, packageId, resolveRunId, planId);
        const data = await api(`/api/cicd/pd-conflicts/run-status?${qs}`, { signal: controller.signal, retry: false });
        if (!mountedRef.current || controller.signal.aborted) return;
        // Lauf-ID nachtragen, falls per planId gefunden.
        if (data.id && !resolveRunRef.current) resolveRunRef.current = data.id;
        if (data.html_url) setResult(prev => ({ ...prev, run: { ...(prev?.run || {}), id: data.id, html_url: data.html_url } }));
        if (data.status === "completed") {
          clearInterval(timer);
          resolveRunRef.current = null;
          planIdRef.current = null;
          setResult(null);
          setRunning(false);
          fetchConflicts();
        }
      } catch {
        // 404 ist normal, solange der Lauf noch nicht registriert ist: weiter abfragen.
      } finally { inFlight = false; }
    }, POLL_INTERVAL);
    return () => { clearInterval(timer); controller.abort(); };
  }, [running, spaceId, packageId, fetchConflicts]);

  const submit = useCallback(async (actions) => {
    const version = readVersion.current;
    const data = await api("/api/cicd/pd-conflicts/resolve", {
      method: "POST",
      body: JSON.stringify({ spaceId, packageId, runId, actions }),
    });
    // Accepted writes retain ownership after the dialog closes. Ignore only their late UI result.
    if (!mountedRef.current || version !== readVersion.current) return data;
    // Lauf-ID oder Plan-ID für leichtgewichtiges Polling merken.
    resolveRunRef.current = data?.run?.id ?? null;
    planIdRef.current = data?.planId ?? null;
    setResult(data);
    setLastResolveStatus(null);
    setRunning(true);
    return data;
  }, [spaceId, packageId, runId]);

  const hasConflicts = conflicts !== null && conflicts.length > 0;

  return { conflicts, canResolve, loading, readError, running, result, targetStage, lastResolveStatus, hasConflicts, submit, refresh: fetchConflicts };
}

// Reuse the same observed read error in package details and the nested sync dialog.
export function PdConflictReadNotice({ error, onRetry }) {
  const { t } = useTranslation("freigabe");
  if (!error) return null;
  const inactive = [...new Set((error.issues || [])
    .filter(issue => issue.code === "EDGE_LOCATION_INACTIVE" && typeof issue.runtime === "string")
    .map(issue => issue.runtime))];
  const cloudClear = (error.runtimeChecks || []).some(check => check.runtime === "cloud"
    && check.state === "checked" && check.conflictCount === 0);
  return <Alert severity="warning" variant="outlined"
    sx={{ "& .MuiAlert-message": { minWidth: 0, width: "100%" } }}>
    <Stack spacing={1} alignItems="flex-start">
      <Typography variant="body2">{t(error.code === "TRANSPORT_READ_TIMEOUT" || error.name === "AbortError"
        ? "stageBranches.pdConflict.readTimeout" : "stageBranches.pdConflict.readFailed")}</Typography>
      {cloudClear && <Typography variant="body2">{t("stageBranches.pdConflict.cloudChecked")}</Typography>}
      {inactive.map(runtime => <Typography key={runtime} variant="body2" sx={{ overflowWrap: "anywhere" }}>
        {t("stageBranches.pdConflict.edgeInactive", { runtime })}
      </Typography>)}
      {onRetry && <MonitorButton onClick={onRetry} sx={{ whiteSpace: "nowrap" }}>{t("stageBranches.pdConflict.readRetry")}</MonitorButton>}
    </Stack>
  </Alert>;
}

/**
 * Group flat API conflicts by (agency, scheme, id, ownerPid); each group collects its runtimes.
 * Same triplet+owner on multiple runtimes = multiple entries in `runtimes`.
 * Without `runtime` field (v27 API) the entry defaults to "cloud".
 */
export function groupConflicts(conflicts) {
  const map = new Map();
  for (const c of conflicts || []) {
    const key = [c.agency, c.scheme, c.id, c.ownerPid].join("\0");
    if (!map.has(key)) {
      map.set(key, {
        key: `${c.agency}:${c.scheme}:${c.id}:${c.ownerPid}`,
        agency: c.agency, scheme: c.scheme, id: c.id,
        ownerPid: c.ownerPid, wantedPid: c.wantedPid,
        runtimes: [],
      });
    }
    map.get(key).runtimes.push({
      runtime: c.runtime || "cloud",
      runtimeName: c.runtimeName || null,
      owner: c.owner,
      resolvable: c.resolvable,
      resolution: c.resolution || "remove",
      deprecateOld: c.deprecateOld,
    });
  }
  return [...map.values()];
}

/** Beschriftung für einen Runtime-Eintrag. */
function rtLabel(rt, t) {
  return rt.runtime === "cloud" ? t("stageBranches.pdConflict.runtimeCloudLabel") : (rt.runtimeName || rt.runtime);
}

/** Warntext für die Löschoption (v29: Löschen nur auf der Cloud). */
function deleteWarningForScope(group, scope, t) {
  return t("stageBranches.pdConflict.deleteWarning");
}

/**
 * Kartenzeile: nur ein kurzer Text, kein Knopf.
 * "Ursache: Konflikt im Partner Directory, in den Details lösen" oder "Bereinigung im Partner Directory läuft".
 */
export function PdConflictCardLine({ pdConflict }) {
  const { t } = useTranslation("freigabe");
  if (!pdConflict || pdConflict.loading) return null;
  if (pdConflict.running && pdConflict.hasConflicts) {
    return <Typography variant="body2" role="status" sx={{ color: v("accent"), overflowWrap: "anywhere" }}>
      {t("stageBranches.pdConflict.running")}
    </Typography>;
  }
  if (!pdConflict.hasConflicts) return null;
  return <Typography variant="body2" role="status" sx={{ color: v("warning"), overflowWrap: "anywhere" }}>
    {t("stageBranches.pdConflict.cardHint")}
  </Typography>;
}

/**
 * Fehlerabschnitt: zeigt Zusammenfassung und Zeilen einer fehlgeschlagenen Bereinigung.
 * MUI Alert severity="error", normale Schrift, umbrechend, Link zum Lauf, Zeitpunkt.
 */
function LastResolveFailedSection({ status, t }) {
  if (!status) return null;
  const date = status.createdAt ? new Date(status.createdAt).toLocaleString() : null;
  return <Alert severity="error" variant="outlined" sx={{
    borderColor: "var(--iio-critical)", color: "var(--iio-text-1)", backgroundColor: "transparent",
    "& .MuiAlert-icon": { color: "var(--iio-critical)" },
  }}>
    <Stack spacing={0.5}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {t("stageBranches.pdConflict.lastResolveFailedTitle")}
      </Typography>
      {status.errorSummary && <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
        {status.errorSummary}
      </Typography>}
      {status.errors?.length > 0 && status.errors.map((line, i) =>
        <Typography key={i} variant="body2" sx={{ overflowWrap: "anywhere", color: "var(--iio-text-2)" }}>
          {line}
        </Typography>)}
      {status.errorsUnavailable && <Typography variant="body2" sx={{ color: "var(--iio-text-2)" }}>
        {t("stageBranches.pdConflict.lastResolveErrorsUnavailable")}
      </Typography>}
      {status.html_url && <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
        <a href={status.html_url} target="_blank" rel="noopener noreferrer" style={{ color: v("accent") }}>
          {t("stageBranches.pdConflict.viewRun")} <OpenInNewRounded sx={{ fontSize: 14, verticalAlign: "middle" }}/>
        </a>
      </Typography>}
      {date && <Typography variant="caption" sx={{ color: "var(--iio-text-3)" }}>
        {t("stageBranches.pdConflict.lastResolveFailedAt", { date })}
      </Typography>}
    </Stack>
  </Alert>;
}

/**
 * Abschnitt im Details-Dialog: Konflikte anzeigen und lösen.
 * Zeigt die Konfliktzeilen gruppiert nach (agency, scheme, id, ownerPid), Auswahl je Gruppe
 * (Umfang + Aktion), Bestätigung, Absende-Knopf, Lauf-Link und Running-Zustand.
 * v28: Unterstützung für mehrere Runtimes (Cloud + Edge).
 * Laufanzeige: stageLabelFn zur Beschriftung der Server-Stufe, Fehlerabschnitt bei lastResolve-Fehlschlag.
 */
export function PdConflictSection({ pdConflict, targetStageLabel, stageLabelFn }) {
  const { t } = useTranslation("freigabe");
  const [choices, setChoices] = useState({});
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [localResult, setLocalResult] = useState(null);
  // Welche Runtimes beim letzten Absenden betroffen waren (für den Hinweis nach dem Lauf).
  const [resolvedScopes, setResolvedScopes] = useState(null);

  if (pdConflict?.loading) return <Stack spacing={1} aria-busy="true">
    <Typography>{t("stageBranches.pdConflict.readLoading")}</Typography>
    <LinearProgress aria-label={t("stageBranches.pdConflict.readLoading")}/>
  </Stack>;
  if (pdConflict?.readError) return <PdConflictReadNotice error={pdConflict.readError} onRetry={pdConflict.refresh}/>;
  if (!pdConflict || !pdConflict.hasConflicts) return null;

  const { conflicts, canResolve, running, result, lastResolveStatus } = pdConflict;
  // Stufe aus der Server-Antwort (targetStage) hat Vorrang; stageLabelFn übersetzt den Schlüssel
  // in die Board-Beschriftung. Fallback auf den bisherigen targetStageLabel (Karten-Prop).
  const serverStage = pdConflict.targetStage;
  const stageText = serverStage
    ? (typeof stageLabelFn === "function" ? stageLabelFn(serverStage) : serverStage)
    : (targetStageLabel || "");
  const shownResult = localResult || result;
  const groups = groupConflicts(conflicts);

  // Beim ersten Rendern die Auswahl je Gruppe initialisieren (bestehende Technik aus v27).
  if (Object.keys(choices).length === 0 && groups.length > 0 && !running) {
    const initial = {};
    groups.forEach((group, i) => {
      const resolvable = group.runtimes.filter(r => r.resolvable !== false);
      initial[i] = {
        action: "remove",
        scope: resolvable.length > 1 ? "all" : (resolvable[0]?.runtime || group.runtimes[0]?.runtime || "cloud"),
      };
    });
    Object.assign(choices, initial);
  }

  const updateChoice = (index, patch) => setChoices(prev => ({ ...prev, [index]: { ...(prev[index] || {}), ...patch } }));

  const doSubmit = async () => {
    if (!confirmed || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const scopes = new Set();
      const rawActions = groups.flatMap((group, i) => {
        const choice = choices[i] || { action: "remove", scope: "all" };
        const selectedRts = choice.scope === "all"
          ? group.runtimes.filter(r => r.resolvable !== false)
          : group.runtimes.filter(r => r.runtime === choice.scope && r.resolvable !== false);
        if (!selectedRts.length) return [];
        selectedRts.forEach(r => scopes.add(r.runtime === "cloud" ? "cloud" : "edge"));
        return selectedRts.flatMap(rt => {
          // v29: Edge Runtimes nutzen reassign + optionales mark-deprecated
          if (rt.runtime !== "cloud") {
            const reassign = {
              type: "reassign-alternative-partner", runtime: rt.runtime,
              pid: group.ownerPid, toPid: group.wantedPid,
              agency: group.agency, scheme: group.scheme, id: group.id,
            };
            return rt.deprecateOld !== false
              ? [reassign, { type: "mark-deprecated", runtime: rt.runtime, pid: group.ownerPid }]
              : [reassign];
          }
          // Cloud: remove + optionales delete (unverändert)
          const remove = { type: "remove-alternative-partner", runtime: rt.runtime, pid: group.ownerPid, agency: group.agency, scheme: group.scheme, id: group.id };
          return choice.action === "delete"
            ? [remove, { type: "delete-partner", runtime: rt.runtime, pid: group.ownerPid }]
            : [remove];
        });
      });
      // Dedupliziere delete-partner je (runtime, pid).
      const seen = new Set();
      const actions = [];
      for (const action of rawActions) {
        if (action.type === "delete-partner" || action.type === "mark-deprecated") {
          const key = `${action.type}\0${action.runtime}\0${action.pid}`;
          if (seen.has(key)) continue;
          seen.add(key);
        }
        actions.push(action);
      }
      setResolvedScopes(scopes);
      const data = await pdConflict.submit(actions);
      setLocalResult(data);
      // Nach dem Lauf wird für verbleibende Konflikte (Teillösung) neu ausgewählt und neu bestätigt.
      setChoices({});
      setConfirmed(false);
    } catch (failure) {
      setError(failure?.message || t("stageBranches.pdConflict.resolveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const hasResolvable = groups.some(g => g.runtimes.some(r => r.resolvable !== false));

  // Running-Zustand: zeige Status + Link + Hinweis je Umfang
  if (running) {
    const scopes = resolvedScopes;
    return <Stack component="section" spacing={1} aria-label={t("stageBranches.pdConflict.sectionTitle", { stage: stageText })}>
      <Typography component="h3" variant="subtitle2" sx={{ color: v("text-1"), m: 0 }}>
        {t("stageBranches.pdConflict.sectionTitle", { stage: stageText })}
      </Typography>
      <Typography variant="body2" role="status" sx={{ color: v("accent") }}>
        {t("stageBranches.pdConflict.running")}
      </Typography>
      {shownResult?.run?.html_url && <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
        <a href={shownResult.run.html_url} target="_blank" rel="noopener noreferrer" style={{ color: v("accent") }}>
          {t("stageBranches.pdConflict.viewRun")} <OpenInNewRounded sx={{ fontSize: 14, verticalAlign: "middle" }}/>
        </a>
      </Typography>}
      {scopes ? <>
        {scopes.has("cloud") && <Typography variant="body2" sx={{ color: v("text-2") }}>
          {t("stageBranches.pdConflict.runningRetryHintCloud")}
        </Typography>}
        {scopes.has("edge") && <Typography variant="body2" sx={{ color: v("text-2") }}>
          {t("stageBranches.pdConflict.runningRetryHintEdge")}
        </Typography>}
      </> : <Typography variant="body2" sx={{ color: v("text-2") }}>
        {t("stageBranches.pdConflict.runningRetryHint")}
      </Typography>}
    </Stack>;
  }

  return <Stack component="section" spacing={1.5} aria-label={t("stageBranches.pdConflict.sectionTitle", { stage: stageText })}>
    <Typography component="h3" variant="subtitle2" sx={{ color: v("text-1"), m: 0 }}>
      {t("stageBranches.pdConflict.sectionTitle", { stage: stageText })}
    </Typography>
    {/* Fehlerabschnitt einer vorherigen fehlgeschlagenen Bereinigung (Laufanzeige). */}
    <LastResolveFailedSection status={lastResolveStatus} t={t}/>
    <Typography variant="body2" sx={{ color: v("text-2") }}>
      {t("stageBranches.pdConflict.dialogIntro", { stage: stageText })}
    </Typography>
    <Stack component="ul" spacing={2} sx={{ listStyle: "none", m: 0, p: 0 }}>
      {groups.map((group, index) => {
        const resolvableRts = group.runtimes.filter(r => r.resolvable !== false);
        const notResolvableRts = group.runtimes.filter(r => r.resolvable === false);
        const noneResolvable = resolvableRts.length === 0;
        const choice = choices[index] || { action: "remove", scope: resolvableRts.length > 1 ? "all" : (resolvableRts[0]?.runtime || "cloud") };
        // v29: Cloud und Edge getrennt im gewählten Umfang
        const scopeRts = choice.scope === "all"
          ? resolvableRts
          : resolvableRts.filter(r => r.runtime === choice.scope);
        const cloudInScope = scopeRts.filter(r => r.runtime === "cloud");
        const edgeInScope = scopeRts.filter(r => r.runtime !== "cloud");
        return <li key={group.key}>
          <Stack spacing={1} sx={{ borderTop: index > 0 ? `1px solid ${v("border-subtle")}` : undefined, pt: index > 0 ? 1.5 : 0 }}>
            {/* Konfliktzeile */}
            <Typography variant="body2" sx={{ color: v("text-1"), overflowWrap: "anywhere" }}>
              {t("stageBranches.pdConflict.conflictLine", {
                agency: group.agency, id: group.id, scheme: group.scheme,
                stage: stageText, ownerPid: group.ownerPid, wantedPid: group.wantedPid,
              })}
            </Typography>
            {/* Owner-Zählung je Runtime */}
            {group.runtimes.length > 1
              ? group.runtimes.map(rt => rt.owner && <Typography key={rt.runtime} variant="body2" sx={{ color: v("text-2") }}>
                  {t("stageBranches.pdConflict.ownerCountRuntime", {
                    pid: group.ownerPid, runtimeLabel: rtLabel(rt, t),
                    strings: rt.owner.strings ?? 0, binaries: rt.owner.binaries ?? 0,
                    alternativePartners: rt.owner.alternativePartners ?? 0, authorizedUsers: rt.owner.authorizedUsers ?? 0,
                  })}
                </Typography>)
              : group.runtimes[0]?.owner && <Typography variant="body2" sx={{ color: v("text-2") }}>
                  {t("stageBranches.pdConflict.ownerCount", {
                    pid: group.ownerPid,
                    strings: group.runtimes[0].owner.strings ?? 0, binaries: group.runtimes[0].owner.binaries ?? 0,
                    alternativePartners: group.runtimes[0].owner.alternativePartners ?? 0, authorizedUsers: group.runtimes[0].owner.authorizedUsers ?? 0,
                  })}
                </Typography>}
            {/* Nicht lösbare Runtimes (falls manche lösbar, manche nicht) */}
            {notResolvableRts.length > 0 && resolvableRts.length > 0 && notResolvableRts.map(rt => <Typography key={rt.runtime} variant="body2" sx={{ color: v("warning") }}>
              {t("stageBranches.pdConflict.notResolvableRuntime", { runtimeLabel: rtLabel(rt, t) })}
            </Typography>)}
            {/* Umfang: Nur anbieten wenn canResolve und mehr als eine lösbare Runtime */}
            {canResolve && resolvableRts.length > 1 ? <RadioGroup value={choice.scope} onChange={e => updateChoice(index, { scope: e.target.value })}
              aria-label={t("stageBranches.pdConflict.scopeAll")}>
              {resolvableRts.some(r => r.runtime === "cloud") && <FormControlLabel value="cloud"
                control={<Radio size="small" sx={RADIO_SX}/>}
                label={<Typography variant="body2">{t("stageBranches.pdConflict.scopeCloud")}</Typography>}/>}
              {resolvableRts.filter(r => r.runtime !== "cloud").map(rt => <FormControlLabel key={rt.runtime} value={rt.runtime}
                control={<Radio size="small" sx={RADIO_SX}/>}
                label={<Typography variant="body2">{t("stageBranches.pdConflict.scopeEdge", { name: rt.runtimeName || rt.runtime })}</Typography>}/>)}
              <FormControlLabel value="all"
                control={<Radio size="small" sx={RADIO_SX}/>}
                label={<Typography variant="body2">{t("stageBranches.pdConflict.scopeAll")}</Typography>}/>
            </RadioGroup> : resolvableRts.length === 1 ? <Typography variant="body2" sx={{ color: v("text-2") }}>
              {resolvableRts[0].runtime === "cloud"
                ? t("stageBranches.pdConflict.runtimeCloud")
                : t("stageBranches.pdConflict.runtimeEdge", { name: resolvableRts[0].runtimeName || resolvableRts[0].runtime })}
            </Typography> : null}
            {/* Komplett nicht lösbar */}
            {noneResolvable ? <Typography variant="body2" sx={{ color: v("warning") }}>
              {t("stageBranches.pdConflict.notResolvable")}
            </Typography> : canResolve ? <>
              {/* v29: Aktionswahl nur für Cloud Runtimes im gewählten Umfang */}
              {cloudInScope.length > 0 && <RadioGroup value={choice.action} onChange={e => updateChoice(index, { action: e.target.value })}>
                <FormControlLabel value="remove" control={<Radio size="small" sx={RADIO_SX}/>}
                  label={<Typography variant="body2">{t("stageBranches.pdConflict.optionRemove", { ownerPid: group.ownerPid })}</Typography>}/>
                <FormControlLabel value="delete" control={<Radio size="small" sx={RADIO_SX}/>}
                  label={<Stack spacing={0.25}>
                    <Typography variant="body2">{t("stageBranches.pdConflict.optionDelete", { ownerPid: group.ownerPid })}</Typography>
                    <Typography variant="caption" sx={{ color: v("danger") }}>{deleteWarningForScope(group, choice.scope, t)}</Typography>
                  </Stack>}/>
              </RadioGroup>}
              {/* v29: Edge Erklärungstext je Edge Runtime im Umfang */}
              {edgeInScope.map(rt => <Typography key={rt.runtime} variant="body2" sx={{ color: v("text-2"), overflowWrap: "anywhere" }}>
                {rt.deprecateOld !== false
                  ? t("stageBranches.pdConflict.edgeReassignExplanation", {
                      name: rt.runtimeName || rt.runtime, agency: group.agency, scheme: group.scheme,
                      id: group.id, wantedPid: group.wantedPid, ownerPid: group.ownerPid,
                    })
                  : t("stageBranches.pdConflict.edgeReassignExplanationNoDeprecate", {
                      name: rt.runtimeName || rt.runtime, agency: group.agency, scheme: group.scheme,
                      id: group.id, wantedPid: group.wantedPid, ownerPid: group.ownerPid,
                    })}
              </Typography>)}
            </> : null}
          </Stack>
        </li>;
      })}
    </Stack>
    {canResolve && <>
      <FormControlLabel
        control={<Checkbox checked={confirmed} onChange={e => setConfirmed(e.target.checked)}
          size="small" sx={{ color: "var(--iio-text-2)", "&.Mui-checked": { color: "var(--iio-signal)" } }}/>}
        label={<Typography variant="body2">{t("stageBranches.pdConflict.confirm")}</Typography>}/>
      {error && <Alert severity="error" variant="outlined" sx={{
        borderColor: "var(--iio-critical)", color: "var(--iio-text-1)", backgroundColor: "transparent",
        "& .MuiAlert-icon": { color: "var(--iio-critical)" },
      }}>{error}</Alert>}
      <MonitorButton variant="contained" disableElevation disabled={!confirmed || submitting || !hasResolvable}
        onClick={doSubmit} sx={{ alignSelf: "flex-start" }}>
        {submitting ? t("stageBranches.pdConflict.submitting") : t("stageBranches.pdConflict.submitAction")}
      </MonitorButton>
    </>}
  </Stack>;
}

// Rückwärtskompatibel: Default-Export für den Fall, dass ein Import noch den alten Namen nutzt.
export default function PdConflictResolver() { return null; }
